<script setup>
import { computed, onMounted, ref } from "vue";
import BankingOposService from "@/services/BankingOposService";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { summarizePayments } from "@/utils/paymentSuggestions.cjs";
import BankingMockPanel from "@/components/BankingMockPanel.vue";

const userStore = useUserStore();
const entitlementStore = useEntitlementStore();
const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";
const hasBanking = computed(() => userStore.hasPermission("banking.read") &&
  (disableAuthForE2E || entitlementStore.hasModule("banking")));
const hasOpos = computed(() => userStore.hasPermission("opos.read") &&
  (disableAuthForE2E || entitlementStore.hasModule("opos")));
const accounts = ref([]);
const selectedAccountKey = ref("");
const transactions = ref([]);
const openItems = ref([]);
const suggestions = ref([]);
const loading = ref(true);
const accountLoading = ref(false);
const operationPending = ref(false);
const loadError = ref("");
const operationError = ref("");
const operationNotice = ref("");
const selectedCandidate = ref(null);
const matchRequestKey = ref("");
const manualTransaction = ref(null);
const manualOpenItemId = ref("");
const reversalTarget = ref(null);
const allocationAmount = ref("");
const operationReason = ref("");
const invoiceNumberSearch = ref("");
const registrationDirection = ref("");
const invoiceCandidates = ref([]);
const invoiceSearchPending = ref(false);
const fintsConnections = ref([]);
const selectedFintsConnection = ref("");
const fintsSyncPending = ref(false);
const selectedSyncAccountIds = ref([]);
const lastFintsRun = ref(null);
const selectedFintsDetails = computed(() => fintsConnections.value.find(connection => connection.connectionId === selectedFintsConnection.value));
const fintsAccounts = computed(() => accounts.value.filter(account => account.providerId === `fints:${selectedFintsConnection.value}`));
const manualPaymentItem = ref(null);
const paymentRequestKey = ref("");
const allowManualOverpayment = ref(false);
const reversalRequestKey = ref("");
const journalItem = ref(null);
const journalEntries = ref([]);
const journalCursor = ref(0);
const journalHasMore = ref(false);
const journalLoading = ref(false);
const journalError = ref("");
const paymentSummaries = computed(() => summarizePayments(suggestions.value));

const canManage = computed(() => hasBanking.value && hasOpos.value &&
  userStore.hasPermission("opos.manage"));
const canManageOpos = computed(() => hasOpos.value && userStore.hasPermission("opos.manage"));
const canRegisterInvoice = computed(() => hasOpos.value &&
  (disableAuthForE2E || entitlementStore.hasModule("invoicing")) &&
  userStore.hasPermission("invoices.read") && userStore.hasPermission("opos.manage"));
const canSyncBank = computed(() => hasBanking.value && userStore.hasPermission("banking.sync"));
const selectedAccount = computed(() =>
  accounts.value.find((account) => accountKey(account) === selectedAccountKey.value) || null
);
const unresolvedOpenItems = computed(() =>
  openItems.value.filter((item) => item.needsReview || item.status !== "Paid").length
);
const unallocatedTransactions = computed(() =>
  transactions.value.filter((transaction) => transaction.amount !== 0 && transaction.remainingAmount > 0).length
);
const manualOpenItems = computed(() => openItems.value.filter((item) =>
  !item.needsReview && item.remainingAmount > 0 &&
  item.currencyCode === manualTransaction.value?.currencyCode &&
  item.paymentDirection === (manualTransaction.value?.amount < 0 ? "Outgoing" : "Incoming")
));
const confirmationTarget = computed(() => {
  if (selectedCandidate.value) return selectedCandidate.value;
  const transaction = manualTransaction.value;
  const item = manualOpenItems.value.find((entry) => entry.id === manualOpenItemId.value);
  if (!transaction || !item) return null;
  return {
    openItemId: item.id,
    openItemReference: item.reference,
    providerId: transaction.providerId,
    accountId: transaction.accountId,
    transactionId: transaction.transactionId,
    counterpartyName: transaction.counterpartyName,
    openItemRemainingAmount: item.remainingAmount,
    transactionRemainingAmount: transaction.remainingAmount,
    currencyCode: transaction.currencyCode,
  };
});
const maximumAllocation = computed(() => confirmationTarget.value
  ? Math.min(confirmationTarget.value.openItemRemainingAmount, confirmationTarget.value.transactionRemainingAmount)
  : 0);

function accountKey(account) {
  return JSON.stringify([account.providerId, account.accountId]);
}

function dateRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 89);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}

function operationId(prefix) {
  const suffix = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  return `${prefix}-${suffix}`;
}

function formatMoney(value, currency = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency || "EUR",
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "-";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${day}.${month}.${year}`;
}

function paymentCaseLabel(paymentCase) {
  return {
    PartialPayment: "Teilzahlung",
    FullPayment: "Vollzahlung",
    Overpayment: "Überzahlung",
  }[paymentCase] || paymentCase;
}

function statusLabel(status) {
  return {
    Open: "Offen",
    PartiallyPaid: "Teilbezahlt",
    Paid: "Bezahlt",
    Overpaid: "Überbezahlt",
  }[status] || status;
}

function statusClass(status) {
  return {
    Open: "text-bg-warning",
    PartiallyPaid: "text-bg-info",
    Paid: "text-bg-success",
    Overpaid: "text-bg-warning",
  }[status] || "text-bg-secondary";
}

function directionLabel(direction) {
  return direction === "Outgoing" ? "Zahlungsausgang" : "Zahlungseingang";
}

async function loadAccountData() {
  const account = selectedAccount.value;
  if (!account) {
    transactions.value = [];
    suggestions.value = [];
    return;
  }

  accountLoading.value = true;
  loadError.value = "";
  try {
    const range = dateRange();
    const [transactionData, suggestionData] = await Promise.all([
      BankingOposService.getTransactions(
        account.providerId,
        account.accountId,
        range.from,
        range.to
      ),
      hasOpos.value ? BankingOposService.getMatchSuggestions(
        account.providerId,
        account.accountId,
        range.from,
        range.to
      ) : Promise.resolve([]),
    ]);
    transactions.value = transactionData;
    suggestions.value = suggestionData;
  } catch (error) {
    loadError.value = errorMessage(error, "Kontoumsätze und Vorschläge konnten nicht geladen werden.");
  } finally {
    accountLoading.value = false;
  }
}

async function loadWorkspace(showLoading = true) {
  if (showLoading) loading.value = true;
  loadError.value = "";
  operationError.value = "";
  try {
    if (!userStore.me) {
      await userStore.loadMe();
    }
    const previousAccountKey = selectedAccountKey.value;
    const [accountData, openItemData, connectionData] = await Promise.all([
      hasBanking.value ? BankingOposService.getAccounts() : Promise.resolve([]),
      hasOpos.value ? BankingOposService.getOpenItems() : Promise.resolve([]),
      hasBanking.value ? BankingOposService.getFintsConnections() : Promise.resolve([]),
    ]);
    accounts.value = accountData;
    selectedSyncAccountIds.value = selectedSyncAccountIds.value.filter(id => fintsAccounts.value.some(account => account.accountId === id && account.isActive));
    openItems.value = openItemData;
    fintsConnections.value = connectionData;
    if (!connectionData.some((connection) => connection.connectionId === selectedFintsConnection.value)) {
      selectedFintsConnection.value = connectionData[0]?.connectionId || "";
    }
    selectedAccountKey.value = accountData.some(
      (account) => accountKey(account) === previousAccountKey
    )
      ? previousAccountKey
      : accountData[0]
        ? accountKey(accountData[0])
        : "";
    await loadAccountData();
  } catch (error) {
    loadError.value = errorMessage(error, "Banking-/OPOS-Daten konnten nicht geladen werden.");
  } finally {
    loading.value = false;
  }
}

async function searchInvoices() {
  invoiceSearchPending.value = true;
  operationError.value = "";
  try {
    invoiceCandidates.value = await BankingOposService.findInvoiceCandidates(invoiceNumberSearch.value.trim());
  } catch (error) {
    invoiceCandidates.value = [];
    operationError.value = errorMessage(error, "Rechnungen konnten nicht gesucht werden.");
  } finally {
    invoiceSearchPending.value = false;
  }
}

async function registerInvoice(candidate) {
  if (!registrationDirection.value) {
    operationError.value = "Bitte zuerst die Zahlungsrichtung wählen.";
    return;
  }
  operationPending.value = true;
  operationError.value = "";
  operationNotice.value = "";
  try {
    await BankingOposService.registerInvoice(candidate.invoiceId, registrationDirection.value);
    operationNotice.value = `Rechnung ${candidate.invoiceNumber} (${candidate.invoiceId}) wird als ${directionLabel(registrationDirection.value)} verfolgt.`;
    await searchInvoices();
    await loadWorkspace();
  } catch (error) {
    operationError.value = errorMessage(error, "Die Rechnung konnte nicht als offener Posten registriert werden.");
  } finally {
    operationPending.value = false;
  }
}

async function syncBank() {
  if (!canSyncBank.value || !selectedFintsConnection.value || !selectedSyncAccountIds.value.length) return;
  fintsSyncPending.value = true;
  operationError.value = "";
  operationNotice.value = "";
  try {
    const range = dateRange();
    const result = await BankingOposService.startFintsSync({
      connectionId: selectedFintsConnection.value,
      from: range.from,
      to: range.to,
      accountIds: [...selectedSyncAccountIds.value],
    });
    lastFintsRun.value = result;
    operationNotice.value = result?.message || "Bankabruf abgeschlossen.";
    if (result?.status === "completed") await loadWorkspace();
  } catch (error) {
    operationError.value = errorMessage(error, "Der Bankabruf ist fehlgeschlagen.");
  } finally {
    fintsSyncPending.value = false;
  }
}

async function discoverAccounts() {
  if (!canSyncBank.value || !selectedFintsConnection.value) return;
  fintsSyncPending.value = true;
  operationError.value = "";
  try {
    const result = await BankingOposService.discoverFintsAccounts(selectedFintsConnection.value);
    lastFintsRun.value = result;
    operationNotice.value = result.message;
    if (result.status === "completed") await loadWorkspace(false);
  } catch (error) { operationError.value = errorMessage(error, "Die Bankkonten konnten nicht abgerufen werden."); }
  finally { fintsSyncPending.value = false; }
}

async function toggleAccount(account) {
  if (!canSyncBank.value) return;
  fintsSyncPending.value = true;
  operationError.value = "";
  try {
    await BankingOposService.setAccountActive(account.providerId, account.accountId, !account.isActive);
    await loadWorkspace(false);
  } catch (error) { operationError.value = errorMessage(error, "Der Kontostatus konnte nicht geändert werden."); }
  finally { fintsSyncPending.value = false; }
}

function openConfirmation(candidate) {
  matchRequestKey.value = operationId("ui-confirm");
  manualTransaction.value = null;
  selectedCandidate.value = candidate;
  allocationAmount.value = String(candidate.suggestedAmount);
  operationReason.value = "";
  operationError.value = "";
}

function openManualConfirmation(transaction) {
  matchRequestKey.value = operationId("ui-confirm");
  selectedCandidate.value = null;
  manualTransaction.value = transaction;
  manualOpenItemId.value = "";
  allocationAmount.value = "";
  operationReason.value = "";
  operationError.value = "";
}

function selectManualOpenItem() {
  allocationAmount.value = confirmationTarget.value ? String(maximumAllocation.value) : "";
  operationError.value = "";
}

function closeConfirmation() {
  if (operationPending.value) return;
  selectedCandidate.value = null;
  manualTransaction.value = null;
  operationError.value = "";
}

async function confirmMatch() {
  const candidate = confirmationTarget.value;
  if (!candidate) return;
  const amount = Number(allocationAmount.value);
  if (!Number.isFinite(amount) || amount <= 0 || amount > maximumAllocation.value) {
    operationError.value = `Der Zuordnungsbetrag muss zwischen 0,01 und ${formatMoney(maximumAllocation.value, candidate.currencyCode)} liegen.`;
    return;
  }
  operationPending.value = true;
  operationError.value = "";
  operationNotice.value = "";
  try {
    await BankingOposService.confirmMatch({
      openItemId: candidate.openItemId,
      providerId: candidate.providerId,
      accountId: candidate.accountId,
      transactionId: candidate.transactionId,
      amount,
      idempotencyKey: matchRequestKey.value,
      reason: operationReason.value.trim() || null,
    });
    selectedCandidate.value = null;
    manualTransaction.value = null;
    operationNotice.value = `Zuordnung für ${candidate.openItemReference} wurde bestätigt.`;
    await loadWorkspace();
  } catch (error) {
    operationError.value = error?.response?.status === 409
      ? "Die Zuordnung ist nicht mehr aktuell. Daten neu laden und erneut prüfen."
      : errorMessage(error, "Die Zuordnung konnte nicht bestätigt werden.");
  } finally {
    operationPending.value = false;
  }
}

function openReversal(openItem, allocation) {
  if (!(allocation.isManual ? canManageOpos.value : canManage.value)) return;
  reversalTarget.value = { openItem, allocation };
  reversalRequestKey.value = operationId("ui-reverse");
  operationReason.value = "";
  operationError.value = "";
}

function closeReversal() {
  if (operationPending.value) return;
  reversalTarget.value = null;
  operationError.value = "";
}

async function reverseMatch() {
  const target = reversalTarget.value;
  if (!target) return;
  if (target.allocation.isManual && !operationReason.value.trim()) {
    operationError.value = "Bitte einen Stornogrund für die manuelle Zahlung eingeben.";
    return;
  }
  operationPending.value = true;
  operationError.value = "";
  operationNotice.value = "";
  try {
    const reverse = target.allocation.isManual ? BankingOposService.reverseManualPayment : BankingOposService.reverseMatch;
    await reverse(target.allocation.allocationEntryId, {
      idempotencyKey: reversalRequestKey.value,
      reason: operationReason.value.trim() || null,
    });
    reversalTarget.value = null;
    operationNotice.value = `Zuordnung für ${target.openItem.reference} wurde storniert.`;
    await loadWorkspace();
  } catch (error) {
    operationError.value = error?.response?.status === 409
      ? "Die Zuordnung wurde bereits geändert. Daten neu laden und erneut prüfen."
      : errorMessage(error, "Die Zuordnung konnte nicht storniert werden.");
  } finally {
    operationPending.value = false;
  }
}

function openManualPayment(item) {
  if (!canManageOpos.value || item.needsReview || item.remainingAmount <= 0) return;
  manualPaymentItem.value = item;
  allowManualOverpayment.value = false;
  allocationAmount.value = String(item.remainingAmount);
  operationReason.value = "";
  operationError.value = "";
  paymentRequestKey.value = operationId("ui-manual-payment");
}

async function recordManualPayment() {
  const item = manualPaymentItem.value;
  const amount = Number(allocationAmount.value);
  if (!item || !canManageOpos.value || operationPending.value) return;
  if (!Number.isFinite(amount) || amount <= 0 || (!allowManualOverpayment.value && amount > item.remainingAmount) || !operationReason.value.trim()) {
    operationError.value = "Bitte einen Betrag bis zum offenen Rest und einen Zahlungsgrund eingeben.";
    return;
  }
  operationPending.value = true;
  operationError.value = "";
  try {
    await BankingOposService.recordManualPayment(item.id, {
      amount, reason: operationReason.value.trim(), idempotencyKey: paymentRequestKey.value,
      allowOverpayment: allowManualOverpayment.value,
    });
    manualPaymentItem.value = null;
    operationNotice.value = `Zahlung für ${item.reference} wurde erfasst.`;
    await loadWorkspace();
  } catch (error) {
    operationError.value = errorMessage(error, "Die Zahlung konnte nicht erfasst werden.");
  } finally {
    operationPending.value = false;
  }
}

async function loadJournal(item, append = false) {
  if (journalLoading.value) return;
  journalItem.value = item;
  if (!append) { journalEntries.value = []; journalCursor.value = 0; }
  journalLoading.value = true;
  journalError.value = "";
  try {
    const result = await BankingOposService.getPaymentJournal(item.id, journalCursor.value);
    journalEntries.value.push(...result.events);
    journalCursor.value = result.nextCursor;
    journalHasMore.value = result.hasMore;
  } catch (error) {
    journalError.value = errorMessage(error, "Die Zahlungshistorie konnte nicht geladen werden.");
  } finally {
    journalLoading.value = false;
  }
}

onMounted(loadWorkspace);
</script>

<template>
  <div class="banking-page text-start">
    <header class="banking-header">
      <div>
        <h1>Bankkonten und offene Posten</h1>
        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
          <span v-if="hasBanking" class="badge text-bg-light border">
            <i class="bi bi-bank me-1" aria-hidden="true"></i>Bankzugriff nur lesend
          </span>
          <span v-if="hasOpos && !canManageOpos" class="badge text-bg-secondary">Offene Posten nur lesend</span>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm icon-command"
        :disabled="loading || accountLoading"
        title="Daten aktualisieren"
        aria-label="Daten aktualisieren"
        @click="loadWorkspace"
      >
        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
      </button>
    </header>

    <div v-if="loadError" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert" data-testid="banking-load-error">
      <span><i class="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>{{ loadError }}</span>
      <button type="button" class="btn btn-outline-danger btn-sm ms-3" @click="loadWorkspace">Erneut laden</button>
    </div>
    <div v-if="operationNotice" class="alert alert-success" role="status">{{ operationNotice }}</div>
    <div v-if="operationError && !selectedCandidate && !manualTransaction && !reversalTarget && !manualPaymentItem" class="alert alert-danger" role="alert">{{ operationError }}</div>

    <div v-if="loading" class="banking-loading" role="status" data-testid="banking-loading">
      <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>
      Banking-Daten werden geladen
    </div>

    <template v-else-if="!loadError">
      <BankingMockPanel v-if="hasBanking" :can-manage="canSyncBank" @changed="loadWorkspace(false)" />
      <section v-if="hasBanking" class="account-toolbar" aria-labelledby="account-heading">
        <div>
          <h2 id="account-heading">Bankkonto</h2>
          <select
            v-if="accounts.length"
            v-model="selectedAccountKey"
            class="form-select form-select-sm account-select"
            aria-label="Bankkonto auswählen"
            @change="loadAccountData"
          >
            <option v-for="account in accounts" :key="accountKey(account)" :value="accountKey(account)">
              {{ account.displayName || account.accountId }} · {{ account.iban || account.accountId }}
            </option>
          </select>
          <p v-else class="mb-0 text-muted" data-testid="banking-accounts-empty">Keine Bankkonten vorhanden.</p>
        </div>
        <dl v-if="selectedAccount" class="account-meta">
          <div><dt>Provider</dt><dd>{{ selectedAccount.providerId }}</dd></div>
          <div><dt>Währung</dt><dd>{{ selectedAccount.currencyCode }}</dd></div>
          <div><dt>Status</dt><dd>{{ selectedAccount.isActive ? "Aktiv" : "Inaktiv" }}</dd></div>
        </dl>
      </section>

      <section v-if="canSyncBank && fintsConnections.length" class="card p-3 mb-3" aria-labelledby="fints-heading">
        <h2 id="fints-heading" class="h5">FinTS-Konten und Umsätze</h2>
        <p class="small text-muted">Der Abruf liest Konten und Umsätze. Er löst keine Zahlung aus.</p>
        <div class="d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label for="fints-connection" class="form-label">Bankverbindung</label>
            <select id="fints-connection" v-model="selectedFintsConnection" class="form-select form-select-sm" :disabled="fintsSyncPending" @change="selectedSyncAccountIds = []; lastFintsRun = null">
              <option v-for="connection in fintsConnections" :key="connection.connectionId" :value="connection.connectionId">
                {{ connection.bankName || connection.connectionId }} · BLZ {{ connection.bankCode }}
              </option>
            </select>
          </div>
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="!canSyncBank || fintsSyncPending" @click="discoverAccounts">Konten abrufen</button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="!canSyncBank || fintsSyncPending || !selectedSyncAccountIds.length" @click="syncBank">
            {{ fintsSyncPending ? "Bankabruf läuft…" : "Umsätze abrufen" }}
          </button>
        </div>
        <p v-if="selectedFintsDetails" class="small text-muted mt-2">{{ selectedFintsDetails.bic || '' }}<span v-if="selectedFintsDetails.tanProcedure"> · TAN-Verfahren {{ selectedFintsDetails.tanProcedure }}</span><span v-if="selectedFintsDetails.tanMediumName"> · {{ selectedFintsDetails.tanMediumName }}</span></p>
        <p v-if="!fintsAccounts.length" class="small mt-2">Zuerst Konten abrufen, anschließend die Konten für den Umsatzabruf auswählen.</p>
        <fieldset v-else class="mt-2"><legend class="h6">Konten für den Umsatzabruf</legend>
          <div v-for="account in fintsAccounts" :key="accountKey(account)" class="d-flex align-items-center gap-2 mb-2">
            <label class="form-check mb-0"><input v-model="selectedSyncAccountIds" class="form-check-input" type="checkbox" :value="account.accountId" :disabled="!account.isActive || fintsSyncPending" /> {{ account.displayName || account.accountId }} · {{ account.iban || account.accountId }} {{ account.isActive ? '' : '(inaktiv)' }}</label>
            <button class="btn btn-outline-secondary btn-sm" :disabled="fintsSyncPending" @click="toggleAccount(account)">{{ account.isActive ? 'Deaktivieren' : 'Aktivieren' }}</button>
          </div>
        </fieldset>
        <p v-if="lastFintsRun" class="small mt-2 mb-0" role="status">Letzter Abruf: {{ lastFintsRun.message }} · {{ lastFintsRun.accountCount || 0 }} Konten<span v-if="lastFintsRun.transactionCount !== undefined"> · {{ lastFintsRun.transactionCount }} Umsätze</span></p>
      </section>

      <section v-if="canRegisterInvoice" class="card p-3 mb-3" aria-labelledby="invoice-registration-heading">
        <h2 id="invoice-registration-heading" class="h5">Rechnung als offenen Posten aufnehmen</h2>
        <p class="small text-muted">Bei gleichen Rechnungsnummern den richtigen Datensatz anhand von Datum, Betrag und Datensatz-ID auswählen. Die Zahlungsrichtung richtet sich danach, ob Geld auf unserem Konto eingeht oder abgeht; bei Gutschriften besonders prüfen.</p>
        <form class="d-flex flex-wrap gap-2 align-items-end" @submit.prevent="searchInvoices">
          <div>
            <label for="opos-invoice-number" class="form-label">Rechnungsnummer suchen</label>
            <input id="opos-invoice-number" v-model="invoiceNumberSearch" class="form-control form-control-sm" minlength="2" maxlength="100" required />
          </div>
          <button type="submit" class="btn btn-outline-primary btn-sm" :disabled="invoiceSearchPending">Suchen</button>
          <div>
            <label for="opos-payment-direction" class="form-label">Zahlungsrichtung</label>
            <select id="opos-payment-direction" v-model="registrationDirection" class="form-select form-select-sm">
              <option value="" disabled>Bitte auswählen</option>
              <option value="Incoming">Zahlungseingang</option>
              <option value="Outgoing">Zahlungsausgang</option>
            </select>
          </div>
        </form>
        <div v-if="invoiceCandidates.length" class="table-responsive mt-3">
          <table class="table table-sm align-middle">
            <thead><tr><th>Rechnung</th><th>Datum</th><th>Verkäufer / Käufer</th><th class="text-end">Brutto</th><th></th></tr></thead>
            <tbody>
              <tr v-for="candidate in invoiceCandidates" :key="candidate.invoiceId">
                <td><strong>{{ candidate.invoiceNumber }}</strong><small class="d-block text-muted">{{ candidate.isCreditNote ? "Gutschrift" : "Rechnung" }} · {{ candidate.invoiceId }}</small></td>
                <td>{{ formatDate(candidate.invoiceDate) }}</td>
                <td>{{ candidate.sellerName || "-" }} / {{ candidate.buyerName || "-" }}</td>
                <td class="text-end">{{ formatMoney(candidate.grossAmount, candidate.currencyCode) }}</td>
                <td><button type="button" class="btn btn-outline-primary btn-sm" :disabled="candidate.isTracked || operationPending || !registrationDirection" @click="registerInvoice(candidate)">
                  {{ candidate.isTracked ? "Bereits aufgenommen" : "Aufnehmen" }}
                </button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="invoiceNumberSearch && !invoiceSearchPending" class="small text-muted mt-2 mb-0">Suche starten, um passende Rechnungen anzuzeigen.</p>
      </section>

      <div class="status-strip" aria-label="Arbeitsbestand">
        <div v-if="hasOpos"><span>Offene Posten</span><strong>{{ unresolvedOpenItems }}</strong></div>
        <div v-if="hasBanking"><span>Umsätze ohne vollständige Zuordnung</span><strong>{{ unallocatedTransactions }}</strong></div>
        <div v-if="hasBanking && hasOpos"><span>Abgleichvorschläge</span><strong>{{ suggestions.length }}</strong></div>
      </div>

      <div v-if="accountLoading" class="banking-loading compact" role="status">
        <div class="spinner-border spinner-border-sm me-2" aria-hidden="true"></div>
        Konto wird aktualisiert
      </div>

      <div v-else class="workspace-grid">
        <section v-if="hasBanking" aria-labelledby="transactions-heading">
          <div class="section-heading">
            <h2 id="transactions-heading">Kontoumsätze</h2>
            <span class="count-label">{{ transactions.length }}</span>
          </div>
          <div v-if="transactions.length" class="table-responsive">
            <table class="table table-sm align-middle banking-table">
              <thead><tr><th>Valuta</th><th>Gegenpartei / Verwendungszweck</th><th class="text-end">Betrag</th><th class="text-end">Rest</th><th></th></tr></thead>
              <tbody>
                <tr v-for="transaction in transactions" :key="transaction.transactionId">
                  <td class="text-nowrap">{{ formatDate(transaction.valueDate) }}</td>
                  <td><strong>{{ transaction.counterpartyName || "-" }}</strong><small>{{ transaction.remittanceInformation || "-" }}</small></td>
                  <td class="text-end text-nowrap" :class="transaction.amount < 0 ? 'text-danger' : ''">{{ formatMoney(transaction.amount, transaction.currencyCode) }}</td>
                  <td class="text-end text-nowrap">{{ formatMoney(transaction.remainingAmount, transaction.currencyCode) }}</td>
                  <td class="text-end"><button v-if="canManage && transaction.amount !== 0 && transaction.remainingAmount > 0" type="button" class="btn btn-outline-primary btn-sm text-nowrap" @click="openManualConfirmation(transaction)">Manuell zuordnen</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-row" data-testid="banking-transactions-empty">Keine Umsätze im Zeitraum.</p>
        </section>

        <section v-if="hasOpos" aria-labelledby="open-items-heading">
          <div class="section-heading">
            <h2 id="open-items-heading">Offene Posten</h2>
            <span class="count-label">{{ openItems.length }}</span>
          </div>
          <div v-if="openItems.length" class="table-responsive">
            <table class="table table-sm align-middle banking-table">
              <thead><tr><th>Beleg / Gegenpartei</th><th>Richtung</th><th>Fällig</th><th>Status</th><th class="text-end">Rest</th><th></th></tr></thead>
              <tbody>
                <tr v-for="item in openItems" :key="item.id">
                  <td><strong>{{ item.reference }}</strong><small>{{ item.debtorName || "-" }}</small><small class="d-block text-muted">{{ item.id }}</small></td>
                  <td>{{ directionLabel(item.paymentDirection) }}</td>
                  <td class="text-nowrap">{{ formatDate(item.dueDate) }}</td>
                  <td><span class="badge" :class="item.needsReview ? 'text-bg-danger' : statusClass(item.status)">{{ item.needsReview ? 'Prüfen' : statusLabel(item.status) }}</span></td>
                  <td class="text-end text-nowrap">{{ formatMoney(item.remainingAmount, item.currencyCode) }}</td>
                  <td class="text-end">
                    <button class="btn btn-outline-secondary btn-sm text-nowrap mb-1" :disabled="journalLoading" @click="loadJournal(item)">Historie</button>
                    <button v-if="canManageOpos && !item.needsReview && item.remainingAmount > 0" class="btn btn-outline-primary btn-sm text-nowrap mb-1 ms-1" @click="openManualPayment(item)">Zahlung erfassen</button>
                    <button
                      v-for="allocation in (item.activeAllocations || []).filter(entry => entry.isManual ? canManageOpos : canManage)"
                      :key="allocation.allocationEntryId"
                      type="button"
                      class="btn btn-outline-danger btn-sm text-nowrap ms-1 mb-1"
                      :title="`${allocation.isManual ? 'Manuelle Zahlung' : 'Bankzuordnung'}: ${formatMoney(allocation.amount, allocation.currencyCode)}`"
                      @click="openReversal(item, allocation)"
                    >
                      <i class="bi bi-arrow-counterclockwise me-1" aria-hidden="true"></i>Stornieren
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-row" data-testid="banking-open-items-empty">Keine offenen Posten vorhanden.</p>
        </section>
      </div>

      <section v-if="hasBanking && hasOpos" class="suggestions-section" aria-labelledby="suggestions-heading">
        <div class="section-heading">
          <h2 id="suggestions-heading">Match-Vorschläge</h2>
          <span class="count-label">{{ suggestions.length }}</span>
        </div>
        <div v-for="payment in paymentSummaries" :key="payment.key" class="alert alert-info py-2" data-testid="collective-payment">
          <strong>Mögliche Sammelzahlung: {{ payment.transactionId }}</strong>
          <span> · {{ payment.itemCount }} verschiedene Rechnungsdatensätze · offene Beträge zusammen {{ formatMoney(payment.total, payment.currencyCode) }} · verfügbar {{ formatMoney(payment.available, payment.currencyCode) }}.</span>
          <span> Jeden Posten einzeln prüfen und zuordnen. Gleiche Rechnungsnummern können unterschiedliche Datensätze bezeichnen.</span>
        </div>
        <div v-if="suggestions.length">
          <div class="table-responsive d-none d-md-block">
            <table class="table table-sm align-middle banking-table">
              <thead><tr><th>Beleg</th><th>Zahlung</th><th>Fall</th><th>Score</th><th class="text-end">Vorschlag</th><th></th></tr></thead>
              <tbody>
                <tr v-for="candidate in suggestions" :key="`${candidate.openItemId}-${candidate.transactionId}`">
                  <td><strong>{{ candidate.openItemReference }}</strong><small>{{ candidate.debtorName }}</small><small>{{ directionLabel(candidate.paymentDirection) }}</small><small class="d-block text-muted">{{ candidate.openItemId }}</small></td>
                  <td><strong>{{ candidate.counterpartyName }}</strong><small>{{ formatDate(candidate.valueDate) }}</small></td>
                  <td>{{ paymentCaseLabel(candidate.paymentCase) }}</td>
                  <td><span class="score" :aria-label="`${candidate.score} Punkte`">{{ candidate.score }} Pkt.</span></td>
                  <td class="text-end text-nowrap">{{ formatMoney(candidate.suggestedAmount, candidate.currencyCode) }}</td>
                  <td class="text-end">
                    <button v-if="canManage" type="button" class="btn btn-primary btn-sm text-nowrap" @click="openConfirmation(candidate)">
                      <i class="bi bi-link-45deg me-1" aria-hidden="true"></i>Zuordnen
                    </button>
                    <span v-else class="text-muted small">Nur lesen</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mobile-suggestions d-md-none">
            <article v-for="candidate in suggestions" :key="`mobile-${candidate.openItemId}-${candidate.transactionId}`">
              <div class="mobile-suggestion-main">
                <div><strong>{{ candidate.openItemReference }}</strong><small>{{ candidate.debtorName }}</small><small>{{ directionLabel(candidate.paymentDirection) }}</small><small class="d-block text-muted">{{ candidate.openItemId }}</small></div>
                <span class="score">{{ candidate.score }} Pkt.</span>
              </div>
              <dl>
                <div><dt>Zahlung</dt><dd>{{ candidate.counterpartyName }}</dd></div>
                <div><dt>Fall</dt><dd>{{ paymentCaseLabel(candidate.paymentCase) }}</dd></div>
                <div><dt>Vorschlag</dt><dd>{{ formatMoney(candidate.suggestedAmount, candidate.currencyCode) }}</dd></div>
              </dl>
              <button v-if="canManage" type="button" class="btn btn-primary btn-sm w-100" @click="openConfirmation(candidate)">
                <i class="bi bi-link-45deg me-1" aria-hidden="true"></i>Zuordnen
              </button>
              <span v-else class="text-muted small">Nur lesen</span>
            </article>
          </div>
        </div>
        <p v-else class="empty-row" data-testid="banking-suggestions-empty">Keine Match-Vorschläge vorhanden.</p>
      </section>
    </template>

    <div v-if="selectedCandidate || manualTransaction" class="dialog-backdrop" @click.self="closeConfirmation">
      <section class="operation-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-heading">
        <header><h2 id="confirm-heading">{{ manualTransaction ? "Manuelle Zuordnung" : "Zuordnung bestätigen" }}</h2><button type="button" class="btn-close" aria-label="Schließen" :disabled="operationPending" @click="closeConfirmation"></button></header>
        <div v-if="manualTransaction" class="mb-3">
          <p class="small text-muted">Prüfe Rechnungsdatensatz und Bankumsatz vor der Bestätigung. Die Auswahl erfolgt ohne automatischen Treffer.</p>
          <label class="form-label" for="manualOpenItem">Offenen Posten auswählen</label>
          <select id="manualOpenItem" v-model="manualOpenItemId" class="form-select form-select-sm" required @change="selectManualOpenItem">
            <option value="" disabled>Bitte auswählen</option>
            <option v-for="item in manualOpenItems" :key="item.id" :value="item.id">{{ item.reference }} · {{ item.debtorName }} · {{ formatMoney(item.remainingAmount, item.currencyCode) }} · {{ item.id }}</option>
          </select>
          <p v-if="!manualOpenItems.length" class="small text-muted mt-2">Für diese Währung ist kein zuordenbarer offener Posten vorhanden.</p>
        </div>
        <dl v-if="confirmationTarget" class="operation-summary">
          <div><dt>Offener Posten</dt><dd>{{ confirmationTarget.openItemReference }} · {{ confirmationTarget.openItemId }}</dd></div>
          <div><dt>Umsatz</dt><dd>{{ confirmationTarget.counterpartyName }} · {{ confirmationTarget.transactionId }}</dd></div>
          <div><dt>Rest OPOS</dt><dd>{{ formatMoney(confirmationTarget.openItemRemainingAmount, confirmationTarget.currencyCode) }}</dd></div>
          <div><dt>Rest Umsatz</dt><dd>{{ formatMoney(confirmationTarget.transactionRemainingAmount, confirmationTarget.currencyCode) }}</dd></div>
        </dl>
        <div v-if="operationError" class="alert alert-danger py-2" role="alert" data-testid="banking-conflict">{{ operationError }}</div>
        <form @submit.prevent="confirmMatch">
          <label class="form-label" for="allocationAmount">Zuordnungsbetrag</label>
          <div class="input-group input-group-sm mb-3"><input id="allocationAmount" v-model="allocationAmount" class="form-control" type="number" min="0.01" :max="maximumAllocation || undefined" step="0.01" required :disabled="!confirmationTarget" /><span class="input-group-text">{{ confirmationTarget?.currencyCode || manualTransaction?.currencyCode }}</span></div>
          <label class="form-label" for="confirmationReason">Notiz</label>
          <input id="confirmationReason" v-model="operationReason" class="form-control form-control-sm mb-3" maxlength="500" />
          <div class="dialog-actions"><button type="button" class="btn btn-outline-secondary btn-sm" :disabled="operationPending" @click="closeConfirmation">Abbrechen</button><button type="submit" class="btn btn-primary btn-sm" :disabled="operationPending || !confirmationTarget"><span v-if="operationPending" class="spinner-border spinner-border-sm me-1"></span>Bestätigen</button></div>
        </form>
      </section>
    </div>

    <div v-if="manualPaymentItem" class="dialog-backdrop">
      <section class="operation-dialog" role="dialog" aria-modal="true" aria-labelledby="manual-payment-heading">
        <header><h2 id="manual-payment-heading">Zahlung erfassen</h2><button class="btn-close" aria-label="Schließen" :disabled="operationPending" @click="manualPaymentItem = null"></button></header>
        <p>{{ manualPaymentItem.reference }} · {{ directionLabel(manualPaymentItem.paymentDirection) }}</p>
        <p class="small text-muted">Für bereits erfolgte Zahlungen ohne Bankumsatz, zum Beispiel Barzahlungen. Diese Aktion erteilt keinen Zahlungsauftrag.</p>
        <form @submit.prevent="recordManualPayment">
          <label for="manualPaymentAmount" class="form-label">Betrag ({{ manualPaymentItem.currencyCode }})</label>
          <input id="manualPaymentAmount" v-model="allocationAmount" class="form-control mb-3" type="number" min="0.01" :max="allowManualOverpayment ? undefined : manualPaymentItem.remainingAmount" step="0.01" required />
          <label class="form-check mb-3"><input v-model="allowManualOverpayment" type="checkbox" class="form-check-input" /> Eine tatsächlich erhaltene Überzahlung ausdrücklich erfassen</label>
          <label for="manualPaymentReason" class="form-label">Zahlungsgrund / Belegreferenz</label>
          <input id="manualPaymentReason" v-model="operationReason" class="form-control mb-3" maxlength="500" required />
          <p v-if="operationError" class="alert alert-danger" role="alert">{{ operationError }}</p>
          <div class="dialog-actions"><button type="button" class="btn btn-outline-secondary" :disabled="operationPending" @click="manualPaymentItem = null">Abbrechen</button><button class="btn btn-primary" type="submit" :disabled="operationPending">Zahlung bestätigen</button></div>
        </form>
      </section>
    </div>

    <div v-if="journalItem" class="dialog-backdrop">
      <section class="operation-dialog" role="dialog" aria-modal="true" aria-labelledby="journal-heading">
        <header><h2 id="journal-heading">Zahlungshistorie: {{ journalItem.reference }}</h2><button class="btn-close" aria-label="Schließen" :disabled="journalLoading" @click="journalItem = null"></button></header>
        <p v-if="journalError" class="alert alert-danger" role="alert">{{ journalError }}</p>
        <div class="table-responsive"><table class="table table-sm"><thead><tr><th>Datum</th><th>Vorgang</th><th>Betrag</th><th>Grund</th></tr></thead><tbody>
          <tr v-for="entry in journalEntries" :key="entry.entryId"><td>{{ formatDate(entry.occurredAtUtc) }}</td><td>{{ entry.reversesEntryId ? 'Storno' : entry.source === 'manual' ? 'Manuelle Zahlung' : 'Bankzuordnung' }}</td><td>{{ formatMoney(entry.signedAmount, entry.currencyCode) }}</td><td>{{ entry.reason || '-' }}</td></tr>
        </tbody></table></div>
        <p v-if="!journalLoading && !journalEntries.length && !journalError">Noch keine Zahlungen erfasst.</p>
        <button v-if="journalHasMore || journalError" class="btn btn-outline-secondary" :disabled="journalLoading" @click="loadJournal(journalItem, true)">{{ journalError ? 'Erneut laden' : 'Weitere Einträge laden' }}</button>
        <p v-if="journalLoading" role="status">Historie wird geladen…</p>
      </section>
    </div>

    <div v-if="reversalTarget" class="dialog-backdrop" @click.self="closeReversal">
      <section class="operation-dialog" role="dialog" aria-modal="true" aria-labelledby="reverse-heading">
        <header><h2 id="reverse-heading">Zuordnung stornieren</h2><button type="button" class="btn-close" aria-label="Schließen" :disabled="operationPending" @click="closeReversal"></button></header>
        <p><strong>{{ reversalTarget.openItem.reference }}</strong> · {{ formatMoney(reversalTarget.allocation.amount, reversalTarget.allocation.currencyCode) }}</p>
        <div v-if="operationError" class="alert alert-danger py-2" role="alert" data-testid="banking-conflict">{{ operationError }}</div>
        <form @submit.prevent="reverseMatch">
          <label class="form-label" for="reversalReason">Stornogrund</label>
          <input id="reversalReason" v-model="operationReason" class="form-control form-control-sm mb-3" maxlength="500" :required="reversalTarget.allocation.isManual" />
          <div class="dialog-actions"><button type="button" class="btn btn-outline-secondary btn-sm" :disabled="operationPending" @click="closeReversal">Abbrechen</button><button type="submit" class="btn btn-danger btn-sm" :disabled="operationPending"><span v-if="operationPending" class="spinner-border spinner-border-sm me-1"></span>Storno bestätigen</button></div>
        </form>
      </section>
    </div>
  </div>
</template>

<style scoped>
.banking-page {
  color: #20252b;
  margin: 0 auto;
  max-width: 1600px;
  padding: 24px clamp(16px, 3vw, 40px) 48px;
}

.banking-header,
.account-toolbar,
.section-heading,
.operation-dialog header,
.dialog-actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.banking-header { margin-bottom: 20px; }
.banking-header h1 { font-size: 1.5rem; font-weight: 650; margin: 0; }
.icon-command { height: 34px; width: 34px; }

.banking-loading {
  align-items: center;
  background: #f1f4f6;
  border-block: 1px solid #d9dee3;
  display: flex;
  justify-content: center;
  min-height: 180px;
}
.banking-loading.compact { min-height: 64px; }

.account-toolbar {
  background: #f7f8f9;
  border-block: 1px solid #d9dee3;
  gap: 24px;
  padding: 14px 16px;
}
.account-toolbar h2,
.section-heading h2,
.operation-dialog h2 { font-size: 1rem; font-weight: 650; margin: 0; }
.account-select { margin-top: 8px; max-width: 520px; min-width: min(440px, 70vw); }
.account-meta { display: flex; gap: 28px; margin: 0; }
.account-meta div { min-width: 72px; }
.account-meta dt { color: #68717a; font-size: .72rem; font-weight: 600; text-transform: uppercase; }
.account-meta dd { font-size: .9rem; margin: 2px 0 0; }

.status-strip {
  border-bottom: 1px solid #d9dee3;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 26px;
}
.status-strip div { align-items: baseline; display: flex; gap: 10px; padding: 12px 16px; }
.status-strip div + div { border-left: 1px solid #d9dee3; }
.status-strip span { color: #68717a; font-size: .82rem; }
.status-strip strong { font-size: 1.1rem; }

.workspace-grid { display: grid; gap: 32px; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.suggestions-section { border-top: 2px solid #16697a; margin-top: 30px; padding-top: 14px; }
.section-heading { border-bottom: 1px solid #cbd2d8; min-height: 40px; }
.count-label { background: #e7edf0; border-radius: 4px; font-size: .78rem; font-weight: 700; min-width: 28px; padding: 3px 7px; text-align: center; }
.banking-table { font-size: .84rem; margin-bottom: 0; }
.banking-table th { background: #f3f5f6; color: #4b555e; font-size: .72rem; font-weight: 650; text-transform: uppercase; }
.banking-table td { border-color: #e3e7ea; padding-block: 9px; }
.banking-table small { color: #68717a; display: block; margin-top: 2px; }
.empty-row { border-bottom: 1px solid #d9dee3; color: #68717a; margin: 0; padding: 30px 8px; text-align: center; }
.score { background: #dff3e7; border: 1px solid #a6d7b8; border-radius: 4px; color: #185b33; display: inline-block; font-weight: 700; min-width: 62px; padding: 3px 6px; text-align: center; }
.mobile-suggestions article { border-bottom: 1px solid #d9dee3; padding: 14px 4px; }
.mobile-suggestion-main { align-items: flex-start; display: flex; gap: 12px; justify-content: space-between; }
.mobile-suggestion-main small { color: #68717a; display: block; margin-top: 2px; }
.mobile-suggestions dl { display: grid; gap: 10px; grid-template-columns: 1fr 1fr; margin: 12px 0; }
.mobile-suggestions dl div:last-child { grid-column: 1 / -1; }
.mobile-suggestions dt { color: #68717a; font-size: .72rem; text-transform: uppercase; }
.mobile-suggestions dd { font-weight: 600; margin: 2px 0 0; }

.dialog-backdrop { align-items: center; background: rgba(22, 27, 32, .55); display: flex; inset: 0; justify-content: center; padding: 16px; position: fixed; z-index: 1060; }
.operation-dialog { background: #fff; border-radius: 6px; box-shadow: 0 18px 45px rgba(0, 0, 0, .25); max-height: calc(100vh - 32px); max-width: 520px; overflow: auto; padding: 20px; width: 100%; }
.operation-dialog header { border-bottom: 1px solid #d9dee3; margin-bottom: 16px; padding-bottom: 12px; }
.operation-summary { display: grid; gap: 10px 20px; grid-template-columns: 1fr 1fr; }
.operation-summary dt { color: #68717a; font-size: .74rem; }
.operation-summary dd { font-weight: 600; margin: 2px 0 0; }
.dialog-actions { border-top: 1px solid #d9dee3; gap: 8px; padding-top: 14px; }

@media (max-width: 992px) {
  .workspace-grid { grid-template-columns: 1fr; }
  .account-toolbar { align-items: flex-start; flex-direction: column; }
  .account-meta { flex-wrap: wrap; }
}

@media (max-width: 640px) {
  .banking-page { padding-inline: 12px; }
  .status-strip { grid-template-columns: 1fr; }
  .status-strip div + div { border-left: 0; border-top: 1px solid #d9dee3; }
  .account-select { min-width: 0; width: 100%; }
  .operation-summary { grid-template-columns: 1fr; }
}
</style>
